import { useEffect, type RefObject } from 'react';

const REVEAL_SELECTOR = '.section-fade-in, .character-card, .info-card';
const INVIEW_SELECTOR = '.rating-star';

/**
 * One-shot fade/slide reveals plus pause/resume of looping decor when off-screen.
 */
export function useMotionReveal(root: RefObject<HTMLElement | null>, extraKey?: string) {
  useEffect(() => {
    const scope = root.current;
    if (!scope) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealEls = Array.from(scope.querySelectorAll<HTMLElement>(REVEAL_SELECTOR));
    const loopingEls = Array.from(scope.querySelectorAll<HTMLElement>(INVIEW_SELECTOR));

    if (reduceMotion) {
      revealEls.forEach((el) => el.classList.add('is-visible'));
      loopingEls.forEach((el) => el.classList.remove('is-inview'));
      return;
    }

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
    );

    const loopObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-inview', entry.isIntersecting);
        });
      },
      { threshold: 0.01, rootMargin: '40px 0px' }
    );

    revealEls.forEach((el) => revealObserver.observe(el));
    loopingEls.forEach((el) => loopObserver.observe(el));

    return () => {
      revealObserver.disconnect();
      loopObserver.disconnect();
    };
  }, [root, extraKey]);
}
